<template>
  <div class="table-toolbar" v-if="editor">
    <!-- FONT SIZES   -->
    <v-icon>mdi-format-size</v-icon>

    <v-select
        style="width: 4.5rem; margin-left: 0.5rem"
        :items="fontSizesNumbers"
        v-model="fontSizeSelected"
        hide-details
        solo
        dense
        flat
        return-object
    ></v-select>

    <toolbar-textarea-color :editor="editor" close-button="true"/>

    <v-divider vertical></v-divider>

    <!-- TEXT ALIGNMENT   -->
    <tip-tap-toolbar-button
        tooltip="Align left"
        @click="editor.chain().focus().setTextAlign('left').run()"
        :isActive="editor.isActive({ textAlign: 'left' })"
        icon="mdi-format-align-left"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        tooltip="Align center"
        @click="editor.chain().focus().setTextAlign('center').run()"
        :isActive="editor.isActive({ textAlign: 'center' })"
        icon="mdi-format-align-center"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        tooltip="Align right"
        @click="editor.chain().focus().setTextAlign('right').run()"
        :isActive="editor.isActive({ textAlign: 'right' })"
        icon="mdi-format-align-right"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        tooltip="Align justify"
        @click="editor.chain().focus().setTextAlign('justify').run()"
        :isActive="editor.isActive({ textAlign: 'justify' })"
        icon="mdi-format-align-justify"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <v-divider vertical></v-divider>

    <!-- LISTS   -->
    <tip-tap-toolbar-button
        tooltip="Bullet list"
        @click="editor.chain().focus().toggleBulletList().run()"
        :isActive="editor.isActive('bulletList')"
        icon="mdi-format-list-bulleted"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        tooltip="Ordered list"
        @click="editor.chain().focus().toggleOrderedList().run()"
        :isActive="editor.isActive('orderedList')"
        icon="mdi-format-list-numbered"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <order-list-button :editor="editor"/>

    <v-divider vertical></v-divider>

    <!-- STYLE   -->
    <tip-tap-toolbar-button
        icon="mdi-format-bold"
        tooltip="Bold"
        @click="editor.chain().focus().toggleBold().run()"
        :isActive="editor.isActive('bold')"
        :dark="dark"
    >
    </tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        icon="mdi-format-italic"
        tooltip="Italic"
        @click="editor.chain().focus().toggleItalic().run()"
        :isActive="editor.isActive('italic')"
        :dark="dark"
    >
    </tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        icon="mdi-format-underline"
        tooltip="Underline"
        @click="editor.chain().focus().toggleUnderline().run()"
        :isActive="editor.isActive('underline')"
        :dark="dark"
    >
    </tip-tap-toolbar-button>

    <v-divider vertical></v-divider>

    <!-- TABLE   -->
    <tiptap-table-buttons :editor="editor" :dark="dark"></tiptap-table-buttons>

    <v-divider vertical></v-divider>

    <!-- MATH   -->
    <tip-tap-toolbar-button
        tooltip="Subscript"
        @click="setSubscript"
        :isActive="editor.isActive('subscript')"
        icon="mdi-format-subscript"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        tooltip="Superscript"
        @click="setSuperscript"
        :isActive="editor.isActive('superscript')"
        icon="mdi-format-superscript"
        :dark="dark"
    ></tip-tap-toolbar-button>

    <tip-tap-toolbar-button
        tooltip="Math inline"
        @click="editor.chain().focus().toggleFormulaMark().run()"
        :isActive="editor.isActive('formulaMark')"
        icon="mdi-math-integral"
        :dark="dark"
    />
    <tip-tap-toolbar-button
        tooltip="Math box"
        @click="editor.chain().focus().addKatex().run()"
        :isActive="editor.isActive('formulaNode')"
        icon="mdi-math-integral-box"
        :dark="dark"
    />
    <v-divider vertical></v-divider>
  </div>
</template>

<script>
import {Fragment} from 'vue-fragment';
import TipTapToolbarButton from 'plant-common/src/components/PRichText/TipTapToolbarButton';
import {OrderListButton} from 'plant-common';
import {fontSizesNumbers} from '../../../commonInteractive/style/fontSizesNumbers';
import TiptapTableButtons from './TiptapTableButtons.vue';
import ToolbarTextareaColor from '../textarea/ToolbarTextareaColor.vue';

export default {
  name: 'TableToolbar',
  components: {
    Fragment,
    TiptapTableButtons,
    TipTapToolbarButton,
    OrderListButton,
    ToolbarTextareaColor
  },
  props: ['value', 'editor', 'dark'],
  data() {
    return {
      defaultFontSize: 30,
      fontSizeSelected: this.defaultFontSize,
      fontSizesNumbers
    };
  },
  computed: {},
  watch: {
    fontSizeSelected(fontSize) {
      this.applyFontSize(`${fontSize}px`);
    }
  },
  mounted() {
    if (!this.editor) return;

    this.editor.on('selectionUpdate', event => {
      const currentSelection = event.transaction.curSelection;

      const cursorPositionFrom = currentSelection.from;
      const cursorPositionTo = currentSelection.to;

      if (cursorPositionFrom != cursorPositionTo) return;

      this.getSelectionFontSize();
    });
  },
  methods: {
    setSubscript() {
      this.editor.chain().focus().toggleSubscript().run();
      this.editor.chain().focus().unsetSuperscript().run();
    },
    setSuperscript() {
      this.editor.chain().focus().toggleSuperscript().run();
      this.editor.chain().focus().unsetSubscript().run();
    },
    applyFontSize(fontSize) {
      setTimeout(() => {
        if (!this.editor) return;
        this.editor.chain().focus().setFontSize(fontSize).run();
      }, 0);
    },
    getSelectionFontSize() {
      const fontSize = this.editor.getAttributes('textStyle').fontSize;

      if (fontSize) {
        const fontSizeValue = fontSize.replace('px', '');
        this.fontSizeSelected = Number(fontSizeValue);
      } else {
        this.fontSizeSelected = this.defaultFontSize;
      }
    }
  }
};
</script>

<style>
.table-toolbar {
  display: flex;
  align-items: center;
  height: inherit;
}
</style>